// @file Vue composable that serves as a wrapper for @floating-ui/vue
// Features:
// - Support for Vue 2
// - Window resize listeners
// - Floating auto-update

import type { ComputePositionConfig, Middleware, Placement, ReferenceElement } from '@floating-ui/vue'
import { autoUpdate, computePosition, detectOverflow } from '@floating-ui/vue'
import type { CSSProperties, Ref } from 'vue'
import { isRef, onMounted, onUnmounted, ref, unref, watch } from 'vue'

type FloatingStyles = Pick<CSSProperties, 'position' | 'top' | 'left'>

interface FloatingOffset {
  x: number
  y: number
}

interface Styles {
  floatingStyles: Ref<FloatingStyles | undefined>
  currentPlacement: Ref<Placement | undefined>
  /**
   * The raw offset from Floating UI, for callers that apply it as a transform
   * rather than as `top`/`left`.
   */
  floatingOffset: Ref<FloatingOffset | undefined>
}

const useFloating = (
  reference: Ref<ReferenceElement | null | undefined>,
  floating: Ref<HTMLElement | null | undefined>,
  options?: Partial<ComputePositionConfig> | Ref<Partial<ComputePositionConfig>>,
  isPositionLocked?: Ref<boolean>,
): Styles => {
  const floatingStyles = ref<FloatingStyles>()
  const floatingOffset = ref<FloatingOffset>()
  const currentPlacement = ref<Placement>()

  const updateFloatingStylesAsync = async (): Promise<void> => {
    // Don't update if position is locked
    if (isPositionLocked?.value === true) {
      return
    }

    const optionsValue = unref(options)
    if (floating.value != null && reference.value != null) {
      const position = await computePosition(reference.value, floating.value, optionsValue)
      currentPlacement.value = position.placement
      floatingOffset.value = { x: position.x, y: position.y }
      floatingStyles.value = {
        position: position.strategy,
        top: `${position.y}px`,
        left: `${position.x}px`,
      }
    }
  }

  const updateFloatingStyles = (): void => {
    void updateFloatingStylesAsync()
  }

  let cleanUpAutoUpdate: (() => void) | undefined

  watch(
    [reference, floating],
    () => {
      updateFloatingStyles()

      if (floating.value != null && reference.value != null) {
        cleanUpAutoUpdate?.()
        cleanUpAutoUpdate = autoUpdate(reference.value, floating.value, updateFloatingStyles)
      }
    },
    {
      immediate: true,
    },
  )

  if (isRef(options)) {
    watch(options, () => {
      updateFloatingStyles()

      if (floating.value != null && reference.value != null) {
        cleanUpAutoUpdate?.()
        cleanUpAutoUpdate = autoUpdate(reference.value, floating.value, updateFloatingStyles)
      }
    })
  }

  onMounted(() => {
    window.addEventListener('resize', updateFloatingStyles)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateFloatingStyles)
    cleanUpAutoUpdate?.()
  })

  return {
    floatingStyles,
    floatingOffset,
    currentPlacement,
  }
}

/*
 * Custom middlewares
 */

const shiftUpWhenOverflow = (options: { distanceFromViewportEdges?: number } = {}): Middleware => {
  const { distanceFromViewportEdges = 12 } = options
  return {
    name: 'shiftUpWhenOverflow',
    async fn(state) {
      // On devices with system navigation bars (e.g., Android gesture bar), account for the safe area
      // inset so floating elements aren't obscured by device controls.
      const safeAreaBottom =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom')) || 0
      const overflow = await detectOverflow(state, safeAreaBottom > 0 ? { padding: { bottom: safeAreaBottom } } : {})

      // If overflow is positive, we try to move the element up by the overflow amount in pixels
      const adjustedY = state.y - (overflow.bottom > 0 ? overflow.bottom + distanceFromViewportEdges * 2 : 0)

      // if adjustedY is negative where the floating ui will still overflow out of the viewport,
      // then we will make the floating element scrollable, and set the y-coordinate to 0
      const y = adjustedY < 0 ? 0 : adjustedY

      return {
        x: state.x,
        y,
      }
    },
  }
}
export { useFloating, shiftUpWhenOverflow }
export type { FloatingStyles }
